// Supabase persistence for the assistant. Tables come from the assistant migration; until it runs,
// every call reports `missing` and the assistant works without saving

import { supabase } from "@/lib/agents/db"
import type { DisplayMessage, DisplayToolRun } from "./types"
import { redactSecrets } from "./redact"

// The generated types don't know these tables until they're regenerated after the migration
const db = supabase as any

export const RETENTION_DAYS = 60

export interface ConversationSummary {
  id: string
  title: string
  updatedAt: string
}

export interface MemoryNote {
  id: string
  content: string
  shared: boolean // true = whole team, false = only me
  createdAt: string
}

export interface AssistantSkill {
  id: string
  name: string
  description: string
  instructions: string
  scope: "global" | "modules"
  modules: string[]
  trigger: "auto" | "slash"
  enabled: boolean
}

// Flat rather than a union: this project compiles without strictNullChecks, so unions don't narrow
export interface Result<T> { ok: boolean; data?: T; missing?: boolean; error?: string }

function fail<T>(error: any): Result<T> {
  const msg = error?.message || String(error)
  // Table not created yet (PostgREST schema cache miss, or Postgres undefined_table)
  const missing = error?.code === "PGRST205" || error?.code === "42P01" || /could not find the table|does not exist/i.test(msg)
  return { ok: false, missing, error: msg }
}

// Tool results can be whole stores; keep enough to remember what happened
const MAX_RESULT_CHARS = 1500

function trimRun(t: DisplayToolRun): DisplayToolRun {
  let result = t.result
  try {
    const s = JSON.stringify(result)
    if (s && s.length > MAX_RESULT_CHARS) result = s.slice(0, MAX_RESULT_CHARS) + "…"
  } catch { result = String(result) }
  return { id: t.id, toolName: t.toolName, args: t.args, status: t.status, result, at: t.at }
}

// RLS hides a row it won't let you delete, so no error comes back: check that one was deleted
async function deleteById(table: string, id: string): Promise<Result<null>> {
  const { data, error } = await db.from(table).delete().eq("id", id).select("id")
  if (error) return fail(error)
  if (!data?.length) return { ok: false, error: "Nothing was deleted: it doesn't exist, or your role can't delete it." }
  return { ok: true, data: null }
}

export async function listConversations(teamId: string): Promise<Result<ConversationSummary[]>> {
  const { data, error } = await db.from("assistant_conversation")
    .select("id, title, updated_at").eq("team_id", teamId)
    .order("updated_at", { ascending: false }).limit(100)
  if (error) return fail(error)
  return { ok: true, data: (data ?? []).map((r: any) => ({ id: r.id, title: r.title, updatedAt: r.updated_at })) }
}

export async function createConversation(teamId: string, title: string, route: string): Promise<Result<string>> {
  const { data, error } = await db.from("assistant_conversation")
    .insert({ team_id: teamId, title, last_route: route }).select("id").single()
  if (error) return fail(error)
  return { ok: true, data: data.id }
}

export async function touchConversation(id: string, patch: { title?: string; route?: string; context?: unknown }) {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() }
  if (patch.title !== undefined) row.title = patch.title
  if (patch.route !== undefined) row.last_route = patch.route
  if (patch.context !== undefined) row.context = patch.context
  const { error } = await db.from("assistant_conversation").update(row).eq("id", id)
  return error ? fail(error) : { ok: true as const, data: null }
}

export const deleteConversation = (id: string) => deleteById("assistant_conversation", id)

export async function loadMessages(conversationId: string): Promise<Result<DisplayMessage[]>> {
  const { data, error } = await db.from("assistant_message")
    .select("id, role, content, tools, route").eq("conversation_id", conversationId).order("seq")
  if (error) return fail(error)
  return {
    ok: true,
    data: (data ?? []).map((r: any) => ({ id: r.id, role: r.role, content: r.content ?? "", tools: r.tools ?? [], route: r.route ?? undefined })),
  }
}

export async function appendMessage(conversationId: string, teamId: string, seq: number, m: DisplayMessage) {
  const { error } = await db.from("assistant_message").insert({
    conversation_id: conversationId,
    team_id: teamId,
    seq,
    role: m.role,
    content: m.content,
    tools: (m.tools ?? []).map(trimRun),
    route: m.route ?? null,
  })
  return error ? fail(error) : { ok: true as const, data: null }
}

export async function searchMessages(teamId: string, query: string): Promise<Result<{ conversationId: string; title: string; role: string; content: string; at: string }[]>> {
  const { data, error } = await db.from("assistant_message")
    .select("conversation_id, role, content, created_at, assistant_conversation!inner(title)")
    .eq("team_id", teamId).ilike("content", `%${query.replace(/[\\%_]/g, c => `\\${c}`)}%`)
    .order("created_at", { ascending: false }).limit(20)
  if (error) return fail(error)
  return {
    ok: true,
    data: (data ?? []).map((r: any) => ({
      conversationId: r.conversation_id,
      title: r.assistant_conversation?.title ?? "",
      role: r.role,
      content: String(r.content ?? "").slice(0, 400),
      at: r.created_at,
    })),
  }
}

export async function listMemory(teamId: string): Promise<Result<MemoryNote[]>> {
  const { data, error } = await db.from("assistant_memory")
    .select("id, content, user_id, created_at").eq("team_id", teamId)
    .order("created_at", { ascending: false }).limit(60)
  if (error) return fail(error)
  return { ok: true, data: (data ?? []).map((r: any) => ({ id: r.id, content: r.content, shared: r.user_id == null, createdAt: r.created_at })) }
}

export async function saveMemory(teamId: string, content: string, shared: boolean): Promise<Result<string>> {
  const { data, error } = await db.from("assistant_memory")
    // user_id defaults to the signed-in user; null makes the note team-wide
    .insert(shared ? { team_id: teamId, content, user_id: null } : { team_id: teamId, content })
    .select("id").single()
  if (error) return fail(error)
  return { ok: true, data: data.id }
}

export const deleteMemory = (id: string) => deleteById("assistant_memory", id)

export async function listSkills(teamId: string): Promise<Result<AssistantSkill[]>> {
  const { data, error } = await db.from("assistant_skill")
    .select("*").eq("team_id", teamId).order("sort")
  if (error) return fail(error)
  return {
    ok: true,
    data: (data ?? []).map((r: any) => ({
      id: r.id, name: r.name, description: r.description ?? "", instructions: r.instructions ?? "",
      scope: r.scope === "modules" ? "modules" : "global", modules: r.modules ?? [],
      trigger: r.trigger === "slash" ? "slash" : "auto", enabled: r.enabled !== false,
    })),
  }
}

export async function saveSkill(teamId: string, skill: Omit<AssistantSkill, "id"> & { id?: string }, sort: number): Promise<Result<string>> {
  const row = {
    team_id: teamId, name: skill.name, description: skill.description, instructions: skill.instructions,
    scope: skill.scope, modules: skill.modules, trigger: skill.trigger, enabled: skill.enabled, sort,
    updated_at: new Date().toISOString(),
  }
  const q = skill.id
    ? db.from("assistant_skill").update(row).eq("id", skill.id).select("id").single()
    : db.from("assistant_skill").insert(row).select("id").single()
  const { data, error } = await q
  if (error) return fail(error)
  return { ok: true, data: data.id }
}

export const deleteSkill = (id: string) => deleteById("assistant_skill", id)

export async function recordAction(row: {
  teamId: string; conversationId?: string; module: string; tool: string; args: unknown; status: string; route: string
}) {
  let args = redactSecrets(row.args)
  try {
    const s = JSON.stringify(args)
    if (s && s.length > 4000) args = { truncated: s.slice(0, 4000) }
  } catch { args = null }
  const { error } = await db.from("assistant_action").insert({
    team_id: row.teamId, conversation_id: row.conversationId ?? null, module: row.module,
    tool: row.tool, args, status: row.status, route: row.route,
  })
  if (error) {
    const f = fail(error)
    if (!f.missing) console.warn("[assistant] action log failed:", error.message)
  }
}
